import cx from "classnames";
import { createPortal } from "react-dom";
import { DndContext, DragOverlay, PointerSensor, useSensor, useSensors } from "@dnd-kit/core";
import { restrictToWindowEdges, snapCenterToCursor } from "@dnd-kit/modifiers";
import { useState, useEffect, useRef } from "react";
import { QFile } from "@coreapp/CoreApp/QFile";
import { Spinner, Loader } from "@theorgchartdev/storybook";

import {
  List,
  Pagination,
  Table,
  Flex,
  Search,
  Icon,
  Text,
  EditableText,
  Button,
  TooltipWrapper,
  usePaginationPageSize,
  usePortalContainer
} from "@theorgchartdev/storybook";
import FileTextRow from "./FileTextRow";
import FileNameTextIcon from "./FileNameTextIcon";
import { GetWSMgr } from "../../../../Utils/StateLib";
import { getLocalizedPaginationStrings, getLocalizedTableHeaderStrings } from "@/Utils/Utils";
import ActionListItem from "../ActionListItem/ActionListItem";
import { getHoverTipContent } from "../../../../Utils/Misc";
import {
  FileDragItem,
  FolderDropItem
} from "@/appComponents/Blocks/Modals/FileManagerModal/FileDragItem";
import { FileDropZone } from "@/appComponents/Blocks/Composites/FileDropZone/FileDropZone";
import { useTopic } from "@/hooks/useTopic";
import { useStatusController } from "@/hooks/useStatusController";
import { usePermissionCheck } from "@/hooks/usePermissionCheck";
import { IsHiddenFolder } from "@coreapp/utils/IsHiddenFolder";
import { appstr } from "@coreapp/utils/appstr";
import { QFileMgr } from "@coreapp/CoreApp/QFileMgr";
import { enumFolderType, kSpecialFolders } from "@coreapp/constants";
import { TABLE_SELECTED_ROW_STYLE } from "@/Utils/Constants";
// File manager is created when files accessed once and then stored at workspace manager. A new QFileMgr is spun up
// each time the file directory system is accessed. This could be done manually via another component like the chart explorer, or
// via a method outside of our react components like QWSMgr DoFileMgr method - in which case the FileDirectory component would not
// initialize a new QFileMgr.

const InitializeFileManager = (mode, startDirectory) => {
  let wsMgr = GetWSMgr();
  let fileMgr = new QFileMgr();
  fileMgr.InitFileMgr(wsMgr, startDirectory, mode);
  wsMgr.fFileMgr = fileMgr;
};

const FileDirectory = ({ mode, startDirectory, onFileClick, onFileSelect, dataTestIdPrefix }) => {
  const portalContainer = usePortalContainer();
  const [fileMgr, setFileMgr] = useState(null);
  const [showFolders, setShowFolders] = useState(true);
  const [launchDirectory, setLaunchDirectory] = useState(startDirectory || "");
  const [currentFiles, setCurrentFiles] = useState(null);
  const [currentSelectedFile, setCurrentSelectedFile] = useState(null);
  const selectionCache = useRef([]);
  const [currentFolders, setCurrentFolders] = useState(null);
  const [currentOpenFolder, setCurrentOpenFolder] = useState(null);

  let defaultFileTableData = { data: [] };
  const [fileTableData, setFileTableData] = useState(defaultFileTableData);
  const [paginationIndices, setPaginationIndices] = useState([]);
  const [favorites, setFavorites] = useState([]);
  const [startPage, setStartPage] = useState(1);
  const { initialPageSize, persistPageSize } = usePaginationPageSize("file-directory-page-size");
  const [searchInput, setSearchInput] = useState("");
  const [activeDragItem, setActiveDragItem] = useState(null);
  const wsMgr = GetWSMgr();

  const defaultFiletypeRestrictList = fileMgr?.GetFileTypeRestrictions();

  const autoPaginateRef = useRef(false);
  const scrolledIntoViewRef = useRef(false);

  const { isLoading, setLoading, setReady, message } = useStatusController();
  const { fileManagerPermissions } = usePermissionCheck();

  // One time effect run that kicks off the initial rfc to return the folder list
  // QFileMgr 'DirResult' method handles the subsequent file retrieval rfc based on a launch directory.
  // State hooks references are attached to QFileMgr instance for async updates to file and folder list, in order to
  // trigger re-renders on folder switching, file list changes, etc. (See QFileMgr for specific usage and key placements).
  useEffect(() => {
    if (!wsMgr.GetFileMgr()) InitializeFileManager(mode, startDirectory);
    let fileMgr = wsMgr.GetFileMgr();
    fileMgr.setCurrentFiles = setCurrentFiles;
    fileMgr.setCurrentFolders = setCurrentFolders;
    fileMgr.setCurrentOpenFolder = setCurrentOpenFolder;
    fileMgr.setCurrentSelectedFile = setCurrentSelectedFile;
    fileMgr.setReady = setReady;
    fileMgr.setLoading = setLoading;
    fileMgr.handleFileClick = handleFileClick;
    setFileMgr(fileMgr);
    fileMgr.CallDirRFC(launchDirectory, true);
    if (localStorage.favoritedDocs) setFavorites(JSON.parse(localStorage.favoritedDocs));
    setStartPage(1);
    return () => {
      wsMgr.fFileMgr = null;
    };
  }, []);

  useTopic("ResetAutoFocus", () => {
    ResetAutoFocus();
  });

  const { canDeleteAllFolderContents } = usePermissionCheck();

  const ResetAutoFocus = () => {
    scrolledIntoViewRef.current = false;
    autoPaginateRef.current = false;
  };

  // set folder and file content reference to render as props later
  const RenderContent = () => {
    RebuildFileTableData();
  };

  const RebuildFileTableData = () => {
    let newFileTableData = {
      data: BuildFileTableData()
    };

    // Localize Types
    newFileTableData.data.map(f => {
      const localizedType = appstr(f.type);
      f.type = localizedType === "String not found!" ? f.type : localizedType;
    });

    if (searchInput.length > 0 && newFileTableData.data.length === 0) {
      if (paginationIndices.length > 0) {
        setPaginationIndices([]);
      }
    }

    setFileTableData(newFileTableData);
  };

  // update content state props when folder or files change
  useEffect(() => {
    ResetAutoFocus();
    RenderContent();
  }, [currentOpenFolder, currentFiles, searchInput]);

  const isCurrentFolder = folder => {
    if (!currentOpenFolder || !folder) return;
    return currentOpenFolder.GetFolderName() === folder.GetFolderName();
  };

  const handleFolderSelect = target => {
    fileMgr.FolderSelected(target.folder);
    if (!isCurrentFolder(target.folder)) {
      clearSelection();
    }
  };

  const renameFolder = (newFolderName, eventInfo) => {
    if (eventInfo.renameFolder == null) return;
    const targetFolder = eventInfo.renameFolder;
    const currentFolderName = targetFolder.GetFolderName();
    fileMgr.DoRenameFolder(currentFolderName, newFolderName);
  };

  const renameFile = (newFileName, eventInfo) => {
    if (eventInfo.renameFile == null) return;
    const targetFile = eventInfo.renameFile;
    const targetFileExt = targetFile.GetFileExt();
    const currentFileName = targetFile.GetFileName(true);

    const newFullName =
      targetFileExt.length > 0 ? newFileName + "." + targetFile.GetFileExt() : newFileName;

    const oldFile = targetFile;
    const wasSelected = selectionCache.current.includes(oldFile);
    const wasCurrentlySelected = currentSelectedFile?.GetFileName(true) === currentFileName;

    fileMgr.DoRenameFile(currentFileName, newFullName, () => {
      if (wasSelected || wasCurrentlySelected) {
        // Get the fresh file list from the file manager instead of stale React state
        const freshFileList = fileMgr.fFileList;
        const newFile = freshFileList?.find(file => file.GetFileName(true) === newFullName);
        if (newFile) {
          if (wasSelected) {
            const oldFileIndex = selectionCache.current.indexOf(oldFile);
            if (oldFileIndex > -1) {
              selectionCache.current[oldFileIndex] = newFile;
            }
          }

          if (wasCurrentlySelected) {
            handleFileClick({ value: newFile });
          }
        }
      }
    });
  };

  const CanModifyFolder = folder => {
    return !(folder.IsLocked() || fileMgr.IsSpecialFolder(folder));
  };

  const handleFolderAction = (mode, folder) => {
    clearSelection();
    switch (mode) {
      case "add":
        fileMgr.AddNewFolder();
        break;
      case "reload":
        fileMgr.RefreshCurrentDirectory(folder);
        break;
      case "delete":
        if (fileMgr.CanDeleteCurrentFolder(folder)) fileMgr.DeleteSelectedFolder(folder);
        break;
    }
  };

  const handleFileAction = (mode, file) => {
    switch (mode) {
      case "upload":
        wsMgr.UploadFile();
        break;
      case "duplicate":
        fileMgr.DuplicateSelectedFiles(file);
        break;
      case "download":
        fileMgr.DownloadFile(file);
        break;
      case "delete":
        fileMgr.DoDeleteSelectedFiles(false, file);
        break;
    }
  };

  const GetFolderIcon = (folder, isOver) => {
    const folderOpenIcon = folder.IsLocked() ? "folder-open-lock-duotone" : "folder-open-solid";
    const folderDeleteIcon = "trash-can";

    let folderIcon = folder.IsLocked() ? "folder-lock-duotone" : "folder-solid";
    if (isCurrentFolder(folder) || isOver) folderIcon = folderOpenIcon;
    if (folder.GetFolderType() === enumFolderType.trash) folderIcon = folderDeleteIcon;

    return folderIcon;
  };

  // wrapper for building folder list normalized options prop
  // QFolders => list options
  const BuildFolderOptions = () => {
    var folderOptions = [];
    if (!currentFolders) return folderOptions;
    for (let folder of currentFolders) {
      const canModifyFolder = CanModifyFolder(folder);

      var folderOption = {};
      folderOption.value = folder.GetFolderName();
      folderOption.folder = folder;
      folderOption.clickHandler = handleFolderSelect;

      const isLocked = folder.IsLocked();
      let baseIconClass = "ocn-folder-directory-icon";
      let baseTextClass = "ocn-folder-row-text";
      let isSelected = isCurrentFolder(folder);
      let colorModifierClass = !isLocked ? "ocn-folder-directory-icon--orange" : undefined;
      let activeTextClass = isSelected
        ? "ocn-file-row-active-text ocn-file-row-active-text--bold"
        : null;
      let folderActions = [
        {
          iconName: "arrows-rotate",
          iconProps: { tooltipProps: getHoverTipContent("RefreshDir") },
          buttonProps: {
            ariaLabel:
              getHoverTipContent("RefreshDir").title ?? getHoverTipContent("RefreshDir").message
          },
          onClick: () => handleFolderAction("reload", folder),
          dataTestId: dataTestIdPrefix ? `${dataTestIdPrefix}-folder-reload-btn` : undefined
        }
      ];
      if (canModifyFolder && fileMgr.CanDeleteCurrentFolder(folder))
        folderActions.push({
          iconName: "trash-can",
          iconProps: { color: "error" },
          buttonProps: { ariaLabel: appstr("Delete") },
          onClick: () => handleFolderAction("delete", folder),
          dataTestId: dataTestIdPrefix ? `${dataTestIdPrefix}-folder-delete-btn` : undefined
        });

      // We make sure to localize System folders.
      let folderDisplayName = folder.GetFolderDisplayName();
      if (kSpecialFolders.includes(folderDisplayName.toLowerCase())) {
        folderDisplayName = appstr(folderDisplayName);
      }
      folderOption.ariaLabel = folderDisplayName;

      const textContent = canModifyFolder ? (
        <EditableText
          size={"small"}
          bold={false}
          text={folderDisplayName}
          onChange={renameFolder}
          eventInfo={{ renameFolder: folder }}
          customClass={cx(baseTextClass, activeTextClass)}
        />
      ) : (
        <Text className={cx(baseTextClass, activeTextClass)} size={"small"}>
          {folderDisplayName}
        </Text>
      );

      folderOption.content = (
        <FileDropZone
          targetDirectory={folder?.GetFolderName()}
          allowMultiUpload={true}
          extensionRestrictions={defaultFiletypeRestrictList}
          id={`folder-drop-zone-${folder.GetFolderDisplayName()}`}
        >
          {({ isOver }) => {
            let dropOver = isOver;
            return (
              <FolderDropItem id={folder.GetFolderDisplayName()} value={folder}>
                {isOver => (
                  <ActionListItem
                    padding="medium"
                    focusable={false}
                    className={cx(
                      "oc-drop",
                      isOver
                        ? folder?.IsLocked() || activeDragItem?.GetParentFolder()?.IsLocked()
                          ? "oc-drop--invalid"
                          : "oc-drop--move"
                        : null
                    )}
                    contentBefore={
                      <Flex width={"100%"} flex={"1"} gap="8px" alignItems={"center"}>
                        {isLocked ? (
                          <TooltipWrapper
                            tooltipProps={{
                              message: appstr("Folder locked")
                            }}
                          >
                            <div className={cx(baseIconClass, colorModifierClass)}>
                              <Icon name={GetFolderIcon(folder, isOver || dropOver)} />
                            </div>
                          </TooltipWrapper>
                        ) : (
                          <div className={cx(baseIconClass, colorModifierClass)}>
                            <Icon name={GetFolderIcon(folder, isOver || dropOver)} />
                          </div>
                        )}
                        <div className="oc-overflow-hidden oc-flex-1">{textContent}</div>
                      </Flex>
                    }
                    actions={folderActions}
                  />
                )}
              </FolderDropItem>
            );
          }}
        </FileDropZone>
      );
      folderOption.padding = false;
      folderOptions.push(folderOption);
    }
    return folderOptions;
  };

  const clearSelection = () => {
    selectionCache.current = [];
    clearSelectedFileTarget();
  };

  const clearSelectedFileTarget = () => {
    fileMgr.FileSelected(null);
    onFileClick(null);
  };

  const handleSelectMultiple = data => {
    let selectedFiles = [...selectionCache.current];

    if (selectedFiles.length === 0) {
      handleFileClick(data);
      return;
    }

    let elements = currentFiles;
    let firstSelectedElem = selectedFiles[0];

    let firstSelectedIndex = elements.indexOf(firstSelectedElem);
    let targetSelectedIndex = elements.indexOf(data.value);

    let newSelection = [];

    let lowerBound = Math.min(firstSelectedIndex, targetSelectedIndex);
    let upperBound = Math.max(firstSelectedIndex, targetSelectedIndex);

    for (let i = lowerBound; i <= upperBound; i++) {
      let includedElem = elements[i];
      newSelection.push(includedElem);
    }

    selectionCache.current = newSelection;
    clearSelectedFileTarget();
  };

  const handleCtrlClick = data => {
    let selectedFiles = [...selectionCache.current];
    let targetSelectedIndex = selectedFiles.indexOf(data.value);

    if (selectedFiles.length === 0) {
      handleFileClick(data);
      return;
    }

    if (targetSelectedIndex > -1) selectedFiles.splice(targetSelectedIndex, 1);
    else selectedFiles.push(data.value);

    selectionCache.current = selectedFiles;
    clearSelectedFileTarget();
  };

  // table click handler for handling file clicks
  const handleFileRowClick = (event, data) => {
    if (event.type === "click") {
      switch (event.detail) {
        // single click file handling
        case 1:
          if (event.shiftKey) {
            handleSelectMultiple(data);
          } else if (event.ctrlKey) {
            handleCtrlClick(data);
          } else {
            handleFileClick(data);
          }
          break;
        case 2:
          // double click file handling
          handleFileSelect(data);
          break;
        default:
      }
    } else if (event.type === "keydown") {
      handleFileSelect(data);
    }
  };

  const handleFileClick = data => {
    let wsMgr = GetWSMgr();
    let fileMgr = wsMgr.fFileMgr;
    fileMgr.FileSelected(data.value);
    selectionCache.current = [data.value];
    onFileClick(data.value);
  };

  const handleFileSelect = data => {
    handleFileClick(data);
    onFileSelect(data.value);
  };

  const handleSearchInput = value => {
    // Intentionally clear any and all current file selection(s)
    // for UX purposes, do not remove - eg. bulk actions being performed on files that may be selected and filtered out, this would be bad UX.
    clearSelection();
    setSearchInput(value);
    setPaginationIndices([]);
  };

  const IsSelectedFile = fileObj => {
    if (!fileObj) return false;
    return (
      fileObj.GetFileName(true) === currentSelectedFile?.GetFileName(true) ||
      selectionCache.current.includes(fileObj)
    );
  };
  /*
  const SkeletonFile = () => {
    return (
      <Flex height={"41px"} width={"100%"}>
        <Skeleton
          animation="wave"
          height={100}
          heightUnits="%"
          variant="text"
          width={100}
          widthUnits="%"
        />
      </Flex>
    );
  };
 */
  const FileLabel = ({ rowState }) => {
    const rowIdx = rowState?.rowIndex;
    const filename = rowState?.data?.filename;
    const ext = rowState?.data?.extension;
    const fileObj = rowState?.data?.value;

    if (!fileObj) return;

    const isSelected = IsSelectedFile(fileObj);
    const parentFolder = fileObj.GetParentFolder();
    return (
      <FileDragItem
        id={parentFolder.GetFolderName() + "_" + fileObj.GetFileName(true)}
        value={fileObj}
      >
        <FileNameTextIcon
          filename={filename}
          parentFolder={parentFolder}
          ext={ext}
          onRename={renameFile}
          handleFileAction={handleFileAction}
          value={fileObj}
          isSelected={isSelected}
          rowIdx={rowIdx}
        />
      </FileDragItem>
    );
  };

  const FileDetails = ({ rowState }) => {
    return <FileTextRow rowState={rowState} isSelected={IsSelectedFile(rowState?.data?.value)} />;
  };

  // wrapper for normalizing file table columns
  const BuildFileTableColumns = () => {
    return [
      {
        field: "filename",
        headerName: appstr("Name"),
        type: "cellRenderer",
        columnWidth: "fifty",
        CellRenderer: FileLabel,
        filterDisabled: true
      },
      {
        field: "type",
        headerName: appstr("Type"),
        type: "cellRenderer",
        columnWidth: "ten",
        columnOptions: { color: "#8b8b91" },
        CellRenderer: FileDetails
      },
      {
        field: "localModTime",
        headerName: appstr("Date"),
        type: "cellRenderer",
        columnWidth: "twenty-five",
        filterDisabled: true,
        columnOptions: {
          color: "#8b8b91",
          getSortValue: o => {
            return new Date(o);
          },
          valueSort: true
        },
        CellRenderer: FileDetails
      }
    ];
  };

  const handleDataChange = newRange => {
    setPaginationIndices(newRange);
  };

  // wrapper for normalizing file data into table data
  const BuildFileTableData = () => {
    var data = [];
    if (currentFiles)
      data = currentFiles.map(item => {
        return { ...item.fileInfo };
      });
    if (searchInput.length > 0)
      data = data.filter(
        item => item?.filename.toLowerCase().lastIndexOf(searchInput.toLowerCase()) !== -1
      );
    return data;
  };

  const gridOptions = {
    // set background colour on every row, this is probably bad, should be using CSS classes
    // set background colour on even rows again, this looks bad, should be using CSS classes
    getRowStyle: (data, _rowIndex, isHovered) => {
      if (IsSelectedFile(data.value)) {
        return TABLE_SELECTED_ROW_STYLE;
      } else if (isHovered) {
        return { background: "#ececed", cursor: "pointer" };
      }
    },
    onRowClick: handleFileRowClick
  };

  let searchPlaceholder = appstr("Search folder");
  if (currentOpenFolder) {
    let folderDisplayName = currentOpenFolder.GetFolderDisplayName();
    folderDisplayName = kSpecialFolders.includes(folderDisplayName.toLowerCase())
      ? appstr(folderDisplayName)
      : folderDisplayName;

    searchPlaceholder = appstr("Search specific folder", { folder: folderDisplayName });
  }

  const toolSize = "medium";

  const handleDragStart = event => {
    const { active, over } = event;
    let targetFile = active?.data?.current;
    setActiveDragItem(targetFile);
  };

  const handleDragEnd = event => {
    const { active, over } = event;
    let destFolder = over?.data?.current;
    let srcFolder = activeDragItem?.GetParentFolder();
    if (destFolder == null || srcFolder == null || destFolder.IsLocked() || srcFolder.IsLocked())
      return;
    MoveFiles(srcFolder, destFolder);
    setActiveDragItem(null);
  };

  const MoveFiles = (srcFolder, destFolder) => {
    if (destFolder && destFolder?.GetFolderName() !== srcFolder?.GetFolderName()) {
      const targetFiles = selectionCache.current.map(item => item?.GetFileName(true));
      fileMgr.MoveFiles(
        [...targetFiles],
        srcFolder.GetFolderName(),
        destFolder.GetFolderName(),
        currentOpenFolder.IsLocked(),
        destFolder.IsLocked()
      );
      clearSelection();
    }
  };

  const deleteAllContents = () => {
    fileMgr.HandleCommand("DeleteAllFiles");
  };

  const enableDeleteAll = () => {
    if (fileMgr == null) return false;
    var curFolder = fileMgr.GetCurrentFolder();
    if (!curFolder || IsHiddenFolder(curFolder.GetFolderName()) || curFolder.IsLocked())
      return false;
    return curFolder.GetNumFiles() > 0;
  };

  const handleDragOver = event => {
    const { over, active } = event;
    let destFolder = over?.data?.current;
    let src = active?.data?.current;
    if (destFolder == null) return;
    if (src instanceof QFile && !selectionCache.current.includes(src)) {
      if (selectionCache.current.length > 0) {
        clearSelection();
      }
      selectionCache.current = [src];
    }
    //fileMgr.FolderSelected(destFolder);
  };

  const mouseSensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8
      }
    })
  );

  let dragPresentationName = "";
  if (activeDragItem) {
    dragPresentationName = activeDragItem.GetFileName();
    if (selectionCache.current.length > 1) {
      dragPresentationName = selectionCache.current.length + " files";
    }
  }

  const canPerformAction = action => {
    return fileManagerPermissions.includes(action);
  };

  //const dummyData = [{ filename: "", extension: "", modtime: "", author: "" }];

  // rendering folder and file states
  return (
    <DndContext
      onDragStart={handleDragStart}
      onDragOver={handleDragOver}
      onDragEnd={handleDragEnd}
      sensors={mouseSensors}
    >
      <Loader.Root>
        <div className={"ocn-file-manager-container"}>
          {createPortal(
            <DragOverlay
              className={"oc-drag-overlay"}
              modifiers={[snapCenterToCursor, restrictToWindowEdges]}
            >
              {activeDragItem != null ? (
                <FileNameTextIcon
                  folder={activeDragItem.GetParentFolder()}
                  filename={dragPresentationName}
                  ext={selectionCache.current.length === 1 ? activeDragItem?.GetFileExt() : ""}
                  value={activeDragItem}
                  showActions={false}
                  isSelected={true}
                />
              ) : null}
            </DragOverlay>,
            portalContainer ?? document.body
          )}
          <Flex direction={"column"} height={"100%"} width={"25%"}>
            <div className={"ocn-file-manager-folder-container"}>
              <Flex padding={"12px 16px"}>
                <Button
                  iconProps={{ name: "plus" }}
                  onClick={() => handleFolderAction("add")}
                  className={cx("oc-special-secondary", "oc-special-secondary--medium")}
                  modifier={"secondary"}
                  dataTestId={"fm-create-dir-btn"}
                  disabled={!canPerformAction("addfolder")}
                  tooltipProps={
                    !canPerformAction("addfolder")
                      ? {
                          message: appstr("Generic insufficient permissions")
                        }
                      : undefined
                  }
                >
                  {appstr("Create folder")}
                </Button>
              </Flex>
              <div className={cx("ocn-file-manager-folder-table", "ocn-list-scrollbar")}>
                <List
                  enableVirtualization={false}
                  coloredSelection={true}
                  showCheck={false}
                  itemRole="button"
                  value={currentOpenFolder?.GetFolderName()}
                  options={BuildFolderOptions()}
                  listWidth="fill"
                />
                {false && (
                  <Flex gap="12px" direction="column">
                    {Array(25)
                      .fill()
                      .map((_, index) => (
                        <SkeletonFile key={index} />
                      ))}
                  </Flex>
                )}
              </div>
            </div>
          </Flex>
          <Flex direction={"column"} height={"100%"} width={"75%"}>
            <div className={"ocn-file-manager-file-container"}>
              <Flex
                padding={"12px 16px"}
                justifyContent={"space-between"}
                alignItems={"center"}
                gap={"16px"}
              >
                <Search
                  clearable
                  inputProps={{
                    disabled: !currentFiles,
                    style: { fontSize: "14px" },
                    spellCheck: false
                  }}
                  size={toolSize}
                  onInput={handleSearchInput}
                  placeholder={searchPlaceholder}
                  timeout={200}
                  onResetInput={handleSearchInput}
                  dataTestId={"fm-search-input"}
                />
                <Button
                  onClick={() => handleFileAction("upload")}
                  iconProps={{ name: "cloud-arrow-up" }}
                  className={cx("oc-special-secondary", "oc-special-secondary--medium")}
                  modifier={"secondary"}
                  dataTestId={dataTestIdPrefix ? `${dataTestIdPrefix}-upload-btn` : undefined}
                  disabled={currentOpenFolder?.IsLocked()}
                  tooltipProps={
                    currentOpenFolder?.IsLocked()
                      ? {
                          message: appstr("Generic insufficient permissions")
                        }
                      : undefined
                  }
                >
                  {appstr("Upload file")}
                </Button>
              </Flex>
              <FileDropZone
                targetDirectory={currentOpenFolder?.GetFolderName()}
                allowMultiUpload={true}
                extensionRestrictions={defaultFiletypeRestrictList}
                useFlex={true}
              >
                <div className={cx("ocn-file-manager-file-table", "ocn-list-scrollbar")}>
                  <Table
                    scrolledIntoViewRef={scrolledIntoViewRef}
                    rowLookupKey={"filePath"}
                    startSelectedItem={currentSelectedFile?.fileInfo?.filePath}
                    tableLayout={"fixed"}
                    gridOptions={gridOptions}
                    columns={BuildFileTableColumns()}
                    data={fileTableData.data}
                    paginatedIndices={paginationIndices}
                    showHeader={true}
                    containerPadding={true}
                    filterEnabled={true}
                    sortEnabled={true}
                    rowSize="largeR"
                    textSize="mediumT"
                    dataTestId={dataTestIdPrefix ? `${dataTestIdPrefix}-table` : undefined}
                    headerControlProps={{
                      textTags: getLocalizedTableHeaderStrings()
                    }}
                  />
                  {false && (
                    <Flex gap="12px" direction="column">
                      {Array(25)
                        .fill()
                        .map((_, index) => (
                          <SkeletonFile key={index} />
                        ))}
                    </Flex>
                  )}
                </div>
              </FileDropZone>
            </div>
            {canDeleteAllFolderContents && (
              <Flex alignItems={"center"} justifyContent={"center"}>
                <Button
                  iconProps={{ name: "trash" }}
                  modifier={"underline"}
                  size={"medium"}
                  onClick={deleteAllContents}
                  dataTestId={dataTestIdPrefix ? `${dataTestIdPrefix}-delete-all-btn` : undefined}
                  disabled={!enableDeleteAll()}
                >
                  {fileMgr?.IsTrashFolder() ? appstr("Empty trash") : appstr("Move all to trash")}
                </Button>
              </Flex>
            )}
            <div className="directory-pagination" style={{ marginBottom: "0" }}>
              <Pagination
                initialPageSize={initialPageSize}
                onPageSizeChange={persistPageSize}
                autoPaginateRef={autoPaginateRef}
                modifier="full"
                rowLookupKey={"filePath"}
                startSelectedItem={currentSelectedFile?.fileInfo?.filePath}
                data={fileTableData.data}
                onDataChange={handleDataChange}
                customStyle={{ padding: "12px 24px" }}
                startPage={startPage}
                dataTestIdPrefix={dataTestIdPrefix ? `${dataTestIdPrefix}-pagination-` : undefined}
                textTags={getLocalizedPaginationStrings()}
                preserveSelectedPage={searchInput.length === 0}
              />
            </div>
          </Flex>
          {isLoading && (
            <Loader.Container>
              <Loader.Spinner />
              <Loader.Text>{message ?? appstr("Loading")}</Loader.Text>
            </Loader.Container>
          )}
        </div>
      </Loader.Root>
    </DndContext>
  );
};

export default FileDirectory;

/*
/// TODO WIP favorite system waiting on Table improvements for rendering as well as backend handling -
// The purpose of this block was to mock this using local storage, and was not a production solution

const isFavorited = (file) => {
        return favorites.includes(file.filename);
    }

    const toggleFavorite = (file) => {
        let idx = favorites.indexOf(file.filename)
        if (idx < 0)
            favorites.push(file.filename);
        else
            favorites.splice(idx, 1);
        setFavorites(favorites);
        localStorage.favoritedDocs = JSON.stringify(favorites);
    }

    const FavoriteButton = ({rowState, ...file}) => {
        if (file && file.extension !== "oc5" && file.extension !== 'ocd')
            return;
        return ( (isFavorited(file) || rowState.hovered ) ?
            <ToolIcon
              name={ isFavorited(file) ? "star-solid" : "star-regular" }
              shape="round"
              size="medium"
              color={'orange'}
              onClick={()=>
              {toggleFavorite(file)}}
            />
            : null
        );
    }

 */
